import isEmpty from "lodash/isEmpty";
import { api } from "../axios";
import {
  Client,
  ChangeOfBrokerDetailed,
  ClientHoldingPattern,
  ClientsFilter,
} from "../../types";
import { DistributorEmployee } from "../../types/distributor-employees";

interface ClientResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Client[];
  verification_pending_count: number;
  active_count: number;
  inactive_count: number;
  setup_pending_count: number;
  lead_count: number;
  under_review_count: number;
  update_under_review_count: number;
  update_pending_count: number;
  unassigned_client_count: number;
  tax_status_counts: Record<string, number>;
  holding_pattern_counts: Partial<Record<ClientHoldingPattern, number>>;
}

function getAllClients(
  page: number = 1,
  pageSize: number = 10,
  search: string = "",
  filters: ClientsFilter = { status: [], kyc_status: "" },
  sort?: Array<{ id: string; desc: boolean }>
): Promise<ClientResponse> {
  const { priority_assigned_to: priorityAssignedTo, ...restFilters } = filters;
  const parsedFilters = Object.entries(restFilters).reduce(
    (acc, [key, value]) => {
      if (typeof value === "undefined") return acc;
      if (typeof value === "object" && isEmpty(value)) return acc;
      if (Array.isArray(value)) {
        acc[key] = value.join(",");
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );

  return api.get(`clients/`, {
    params: {
      page,
      page_size: pageSize,
      q: search,
      ...parsedFilters,
      ...(priorityAssignedTo !== undefined
        ? { priority_assigned_to: priorityAssignedTo }
        : {}),
      ordering: sort?.map((s) => `${s.desc ? "-" : ""}${s.id}`).join(","),
    },
    paramsSerializer: { indexes: null },
  });
}

function getClientById(id: number | string): Promise<Client> {
  return api.get(`clients/${id}/`);
}

function getClientAssignedEmployee(
  id: number | string
): Promise<DistributorEmployee | Record<string, never>> {
  return api.get(`clients/${id}/assigned_employee/`);
}

function createClient(data: Partial<Client>): Promise<Client> {
  return api.post("clients/", data);
}

function addJointAccount(id: number | string): Promise<Client> {
  return api.post(`clients/${id}/add_joint_account/`);
}

export type SendHolderApprovalLinkResponse = {
  approval_link: string | null;
  message: string;
  is_approved: boolean;
  holder_role: string;
  profile_id: string;
};

function sendHolderApprovalLink(
  id: number | string,
  payload: { investment_account_guid: string; profile_id: string }
): Promise<SendHolderApprovalLinkResponse> {
  return api.post(`clients/${id}/send-approval-link/`, payload);
}

function updateClient(id: number, data: Partial<Client>): Promise<Client> {
  return api.put(`clients/${id}/`, data);
}

function deleteClient(id: number): Promise<void> {
  return api.delete(`clients/${id}/`);
}

export type BulkImportLeadRow = {
  mobile: string;
  name: string;
  pan?: string;
  email?: string;
};

export type BulkImportResultStatus = "created" | "failed";

export type BulkImportResultReason =
  | "duplicate"
  | "validation"
  | "phone_invalid"
  | "missing_required"
  | "internal_error";

/** Per-row outcome from `POST clients/bulk-import/`. */
export type BulkImportRowResult = {
  row: number;
  status: BulkImportResultStatus;
  client_id: string;
  /** Present when `status` is `"failed"` (or when the API includes a reason). */
  reason?: BulkImportResultReason;
  message: string;
  errors: Record<string, unknown>;
};

export type BulkImportClientsResponse = {
  summary: {
    total: number;
    created: number;
    failed: number;
  };
  results: BulkImportRowResult[];
};

function bulkImportClients(payload: {
  rows: BulkImportLeadRow[];
}): Promise<BulkImportClientsResponse> {
  return api.post("clients/bulk-import/", payload);
}

export {
  addJointAccount,
  bulkImportClients,
  createClient,
  deleteClient,
  getAllClients,
  getClientAssignedEmployee,
  getClientById,
  sendHolderApprovalLink,
  updateClient,
};

interface ClientSetupDetails {
  created_at: string;
  distributor_arn_code: string;
  distributor_brand_logo: string | null;
  distributor_name: string;
  distributor_tag_line: string;
  email: string | null;
  id: number;
  modified_at: string;
  kyc_status_remark: string;
  name: string;
  pan: string;
  phone_number: string;
  status: string;
  kyc_status: "S" | "F" | "U";
}

export function getClientSetupDetails(
  code: string
): Promise<ClientSetupDetails> {
  return api.get(`clients/setup_details/`, {
    params: {
      code,
    },
  });
}

export function createChangeOfBrokerRequest(payload: {
  investmentAccountId: string;
  preference: Record<string, number[]>;
}) {
  return api.post(`change-of-broker/`, {
    investment_account_guid: payload.investmentAccountId,
    preference: payload.preference,
  });
}

export function getChangeOfBrokerRequest(clientId: number) {
  return api.get(`change-of-broker/${clientId}/`);
}

export function getChangeOfBrokerDetailedRequest(
  clientId: number
): Promise<ChangeOfBrokerDetailed> {
  return api.get(`change-of-broker/${clientId}/detailed`);
}
